import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Spinner } from "@/components/ui/spinner"

import { useEditTitle } from "../hooks/use-edit-title"

type EditTitleDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  sessionId: string | null
  currentTitle: string
}

export function EditTitleDialog({
  isOpen,
  onOpenChange,
  sessionId,
  currentTitle,
}: EditTitleDialogProps) {
  const { t } = useTranslation()
  const { form, editTitle, editTitleMutation } = useEditTitle({
    currentTitle,
    sessionId,
    onSuccess: () => {
      onOpenChange(false)
    },
    isOpen,
  })

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("pages.ai_parser.edit_title_dialog.title")}</DialogTitle>
          <DialogDescription>
            {t("pages.ai_parser.edit_title_dialog.description")}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(editTitle)}>
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("pages.ai_parser.edit_title_dialog.field_label")}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="w-full"
                      placeholder={t("pages.ai_parser.edit_title_dialog.title_placeholder")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                disabled={editTitleMutation.isPending}
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t("pages.ai_parser.edit_title_dialog.cancel")}
              </Button>
              <Button disabled={editTitleMutation.isPending} type="submit">
                {editTitleMutation.isPending ? (
                  <>
                    <Spinner />
                    {t("pages.ai_parser.edit_title_dialog.saving")}
                  </>
                ) : (
                  t("pages.ai_parser.edit_title_dialog.save_changes")
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
